import {useEffect, useRef, useState} from "react";
import _ from "lodash";
import {app, callCustomerCenter, idba, onair, print} from "../../modules/config.ts";
import {IDBTables} from "../../modules/idb.service.ts";
import {Api} from "../../modules/api.ts";
import {askAlert} from "../../stores/dialog.thunk.ts";
import {useAppDispatch} from "../../stores/hook.ts";
import {useLocation, useNavigate} from "react-router-dom";
import type {ApiResponse, IDBData} from "../../modules/interfaces.ts";
import type {DaoClient} from "../../daos/dao-client.ts";
import type {DaoNotice} from "../../daos/dao-notice.ts";
import type {DaoCategory} from "../../daos/dao-category.ts";
import type {DaoApiAuth} from "../../daos/dao-api-auth.ts";
import {Utils} from "../../modules/utils.ts";
import type {DaoMedia} from "../../daos/dao-media.ts";


function PageLogin() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const passwordRef = useRef<HTMLInputElement>(null);

  const [id, setId] = useState<string>((app.user?.id ?? '') as string);
  const [password, setPassword] = useState<string>((app.user?.password ?? '') as string);
  const [saved, setSaved] = useState<boolean>(app.user?.saved ?? false);
  const [passthru, setPassthru] = useState<boolean>(app.user?.passthru ?? false);
  const [fromReload] = useState<boolean>(location.state?.reloadForce || false);
  const [fromLogout] = useState<boolean>(location.state?.logout || false);

  useEffect(() => {
    setId((app.user?.id ?? '') as string);
    setPassword((app.user?.password ?? '') as string);
    setSaved(app.user?.saved ?? false);
    setPassthru((isMobile() && app.user?.passthru) ?? false);

    if(Utils.iOSDevice()) {

    }

    if((passthru || fromReload) && !fromLogout && !_.isEmpty(id) && !_.isEmpty(password)) {
      (async () => {
        await loginProcess();
      })();
    }
  }, []);

  async function loginProcess() {
    onair.awaikeOnair().then(() => {
      print("awaikeOnair");
    });

    try {
      if(_.isEmpty(id)) {
        await dispatch(askAlert('', '아이디를 입력하세요.'));
        return;
      }

      if(_.isEmpty(password)) {
        await dispatch(askAlert('', '비밀번호를 입력하세요.'));
        return;
      }

      const params = {id, password, saved, passthru} as DaoClient;
      app.user = app.user == null ? params : Object.assign(app.user, (params ?? {})) as DaoClient;

      const rs: ApiResponse<DaoApiAuth> = await Api.login(id, password, app.connect);
      if(rs.statusText !== 'OK' || rs.data.rs !== 1) {
        if(rs.statusText !== 'OK') await dispatch(askAlert('', '서버와 통신이 원활하지 않습니다. 네트웍상태를 확인하세요.'));
        else {
          await dispatch(askAlert('', rs.data.msg ?? '아이디 또는 비밀번호가 올바르지 않습니다.'));
        }
      } else {
        const apiAuth = rs.data;

        // 인터페이스로 환경설정 파일 전달
        try {
          Utils.saveConfig('CLIENT', apiAuth);
        } catch (e) {}

        app.user = Object.assign(app.user, apiAuth.CLIENT ?? {});
        app.auth = apiAuth.AUTH_KEY ?? '';
        app.connect = apiAuth.CONNECTION as unknown as string ?? "0";
        app.connect_date = Utils.toDateString();
        app.ws_endpoint = (apiAuth.SERVER_ENDPOINT ?? '') as string;
        app.transfer = (apiAuth.TRANSFER ?? '') as string;

        app.notices = [];
        const notices = apiAuth.NOTICE ?? [];
        if(notices.length > 0) {
          for(const notice of notices) {
            app.notices.push(
              {
                ...notice,
                no: Number(notice.no)
              } as DaoNotice
            );
          }
        }

        app.categories = [];
        const categories = apiAuth.CHANNEL ?? [];
        if(categories.length > 0) {
          for(const category of categories) {
            app.categories.push(category as DaoCategory);
          }
        }
        app.voices = apiAuth.TTS_VOICES;
        // 데이터 다시 저장
        await app.save(
          await idba.getByField<IDBData>(IDBTables.configs, 'key', 'init')
        );

        /// 채널음원 리스트
        let channelMedias: DaoMedia[]|null = null;

        // 청취중인 채널이 있는경우 스케줄 -> 채널 순으로 조회시 통신 딜레이에 의해
        // 정규방송 -> 채널순의 딜레이가 생김 이를 방지하기 위해 처널 인출 후 스케줄 처리
        // 채널 사용가능인 경우(채널오픈)
        if(app.user.channel === "1") {
          // app.user.price 서버에 기록된 채널정보, onair.options?.channel 로컬에 저장된 채널정보
          const nchannel = Number(app.user.price) || 0;
          const pchannel = Number(onair.options?.channel) || 0;
          // 채널이 다르면 저장
          if(nchannel !== pchannel) {
            if(onair.options !== undefined) {
              onair.options.channel = String(nchannel);
              onair.saveOptions();
            }
          }

          if(nchannel > 0) {
            channelMedias = await onair.channel(String(nchannel));
          }
        }

        // 스케줄처리
        await onair.schedule();
        if(channelMedias !== null && channelMedias.length > 0)
          onair.setSchedule(onair.getPriority('UD'), channelMedias);

        navigate("/main/channel");
      }
    } catch (error) {
      console.error(error);
    }
  }

  function togglePasswordType() {
    if(passwordRef.current) {
      passwordRef.current.type = passwordRef.current.type == 'password' ? 'text' : 'password';
    }
  }

  function isMobile() {
    // App Store에서 받은 iOS 앱을 Mac에서 실행하면 UA가 Mac으로 보고되어
    // isMobileOrTableDevice()가 false가 된다. 네이티브 앱은 자동재생 제약이 없으므로
    // (mediaTypesRequiringUserActionForPlayback = []) 자동로그인을 노출한다.
    return Utils.isMobileOrTableDevice() || Utils.isIosNativeApp();
  }

  return (
    <div className="login-wrap">
      <div className="login">
        <div className="logo">
          <img src={`${app.imagePath}/logo-t.png`} alt="로고" style={{maxHeight: 45}}/>
        </div>
        <form className="login-form">
          <div className="app-version">
            {app.os}.{app.app_version}
          </div>
          <div className="input-group input-st1">
            <input type="text" id="id" placeholder="아이디" value={id} onChange={(e) => setId(e.target.value)}/>
            <button type="button" tabIndex={-1} className="btn-clear" onClick={() => setId("")}><img
              src="/images/icon-del.png" alt=""/></button>
          </div>

          <div className="input-group input-st2">
            <input type="password" id="password" ref={passwordRef} placeholder="비밀번호" value={password}
                   onChange={(e) => setPassword(e.target.value)}/>
            {password.length > 0 && (
              <button type="button" tabIndex={-1} className="btn-show" onClick={() => togglePasswordType()}><img
                src="/images/icon-show.png" alt=""/></button>
            )}
            <button type="button" tabIndex={-1} className="btn-clear" onClick={() => setPassword("")}><img
              src="/images/icon-del.png" alt=""/></button>
          </div>

          <div className="login-checkbox" style={{justifyContent: "space-between"}}>
            <label className="custom-checkbox">
              <input type="checkbox" checked={saved} onChange={(e) => {
                const checked = e.target.checked;
                if(!checked) setPassthru(checked);
                setSaved(checked);
              }}/>
              <span className="checkbox-image"></span>
              <span className="label-text">로그인 정보 저장</span>
            </label>

            {isMobile() && (
              <label className="custom-checkbox">
                <input type="checkbox" checked={passthru} onChange={(e) => {
                  const checked = e.target.checked;
                  if(checked) setSaved(checked);
                  setPassthru(checked)
                }}/>
                <span className="checkbox-image"></span>
                <span className="label-text">자동로그인</span>
              </label>
            )}
          </div>
          <button type="button" className="btn on" onClick={loginProcess}>로그인</button>
        </form>

        <div className="box-wrap">
          <div className="box">
            <h3>기업 회원이신가요?</h3>
            <p>
              본사 계약 고객께서는 고객센터에 문의해 주시면 친절히 답변해 드리겠습니다.
            </p>
            <span><a onClick={callCustomerCenter} className="box-link">고객센터 연결<img src={`${app.imagePath}/icon-box1.png`} alt="" /></a></span>
          </div>
          <div className="box">
            <h3>아직 회원이 아니신가요?</h3>
            <p>
              {app.name} 회원가입 및 이용권 결제 후 플레이어를 사용하실 수 있습니다.
            </p>
            <span><a onClick={callCustomerCenter} className="box-link">고객센터 연결<img src={`${app.imagePath}/icon-box1.png`} alt="" /></a></span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PageLogin;